/**
 * v1.5.2 GastroGest — Insights de ventas (el diferenciador vs Loyverse: POS con
 * analítica integrada). Top/bottom performers, mejores días, hora pico + franjas,
 * y el registro de transacciones filtrable. Reusa DatePeriodFilter (año/mes) y se
 * hidrata de /pos/insights y /pos/insights/transactions (no estado local).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { TrendingUp, TrendingDown, CalendarDays, Clock, Receipt } from 'lucide-react';
import { api } from '../lib/api';
import { DatePeriodFilter, periodToQuery, periodLabel, defaultPeriod, type PeriodValue } from '../components/DatePeriodFilter';

const mxn = (v: any) =>
  `$${Number(v ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const int = (v: any) => Number(v ?? 0).toLocaleString('es-MX');

interface ItemRow { name: string; kind: string; amount: number; units: number; }
interface WeekdayRow { dow: number; label: string; amount: number; orders: number; }
interface HourRow { hour: number; amount: number; orders: number; }
interface FranjaTop { franja: string; amount: number; orders: number; topItems: Array<{ name: string; amount: number }>; }
interface Overview {
  from: string; to: string; totalSales: number; totalOrders: number;
  topByAmount: ItemRow[]; topByUnits: ItemRow[]; bottom: ItemRow[];
  byWeekday: WeekdayRow[]; byHour: HourRow[]; byFranja: FranjaTop[];
  // PR3 (fix) — reservas en línea (Epic Pagos) como serie propia, no mezclada
  // en las ventas de caja ni en el ticket promedio.
  onlineReservations?: { count: number; sales: number; refunds: number; net: number; allBranches: boolean };
}
interface Txn {
  id: string; closedAt: string; tableName: string | null; itemCount: number;
  subtotal: number; taxTotal: number; total: number; tip: number; method: string | null;
}

const METHOD_LABEL: Record<string, string> = { CASH: 'Efectivo', CARD: 'Tarjeta', TRANSFER: 'Transferencia' };
const WEEKDAYS = [{ v: '', l: 'Todos los días' }, { v: '1', l: 'Lunes' }, { v: '2', l: 'Martes' }, { v: '3', l: 'Miércoles' }, { v: '4', l: 'Jueves' }, { v: '5', l: 'Viernes' }, { v: '6', l: 'Sábado' }, { v: '7', l: 'Domingo' }];

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-5 py-3">
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className="font-serif text-2xl text-slate-900 tabular-nums">{value}</p>
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}

function Card({ title, icon, children, action }: { title: string; icon?: any; children: any; action?: any }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-serif text-lg text-slate-900">{icon}{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

export function GastroGestInsights({ sourceModule = 'gastro' }: { sourceModule?: 'gastro' | 'boutique' | 'gym' }) {
  const [period, setPeriod] = useState<PeriodValue>(defaultPeriod());
  const [ov, setOv] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [topMode, setTopMode] = useState<'amount' | 'units'>('amount');

  // Filtros del registro de transacciones (además del período).
  const [weekday, setWeekday] = useState('');
  const [hourFrom, setHourFrom] = useState('');
  const [hourTo, setHourTo] = useState('');
  const [txns, setTxns] = useState<Txn[]>([]);
  const [txnLoading, setTxnLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const qs = new URLSearchParams(periodToQuery(period));
    qs.set('sourceModule', sourceModule);
    api(`/pos/insights?${qs.toString()}`)
      .then((r) => { if (!cancelled) setOv(r as Overview); })
      .catch(() => { if (!cancelled) setOv(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, sourceModule]);

  const loadTxns = useCallback(() => {
    setTxnLoading(true);
    const qs = new URLSearchParams(periodToQuery(period));
    qs.set('sourceModule', sourceModule);
    if (weekday) qs.set('weekday', weekday);
    if (hourFrom) qs.set('hourFrom', hourFrom);
    if (hourTo) qs.set('hourTo', hourTo);
    api(`/pos/insights/transactions?${qs.toString()}`)
      .then((r) => setTxns((r as { rows: Txn[] }).rows ?? []))
      .catch(() => setTxns([]))
      .finally(() => setTxnLoading(false));
  }, [period, weekday, hourFrom, hourTo, sourceModule]);
  useEffect(() => { loadTxns(); }, [loadTxns]);

  const topItems = topMode === 'amount' ? ov?.topByAmount : ov?.topByUnits;
  const avgTicket = ov && ov.totalOrders ? ov.totalSales / ov.totalOrders : 0;
  const bestDay = useMemo(() => ov?.byWeekday?.slice().sort((a, b) => b.amount - a.amount)[0], [ov]);
  const peakHour = useMemo(() => ov?.byHour?.slice().sort((a, b) => b.amount - a.amount)[0], [ov]);
  const businessHours = useMemo(() => ov?.byHour?.filter((h) => h.hour >= 7 && h.hour <= 23) ?? [], [ov]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl tracking-tight text-slate-900">Ventas & Insights</h2>
          <p className="text-sm text-slate-500">Qué vende más, qué no, y cuándo se vende — {periodLabel(period)}.</p>
        </div>
        <DatePeriodFilter value={period} onChange={setPeriod} />
      </div>

      {loading ? <p className="text-sm text-slate-500">Cargando…</p> : !ov ? (
        <p className="text-sm text-slate-400">Sin datos para el período.</p>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tile label="Vendido" value={mxn(ov.totalSales)} hint={`${int(ov.totalOrders)} ventas`} />
            <Tile label="Ticket promedio" value={mxn(avgTicket)} />
            <Tile label="Mejor día" value={bestDay ? bestDay.label : '—'} hint={bestDay ? mxn(bestDay.amount) : ''} />
            <Tile label="Hora pico" value={peakHour ? `${peakHour.hour}:00` : '—'} hint={peakHour ? `${int(peakHour.orders)} ventas` : ''} />
          </div>

          {/* PR3 (fix) — reservas en línea como fila propia (NO en "Vendido"/ticket). */}
          {ov.onlineReservations && (ov.onlineReservations.count > 0 || ov.onlineReservations.net > 0) && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-emerald-200 bg-emerald-50/40 px-5 py-3 text-sm">
              <span className="font-medium text-slate-700">
                Reserva en línea{ov.onlineReservations.allBranches ? ' (todas las sucursales)' : ''}
                <span className="ml-1 text-[11px] text-slate-400">no incluido en ventas de caja</span>
              </span>
              <span className="tabular-nums text-slate-800">
                <b>{int(ov.onlineReservations.count)}</b> reserva(s) · <b>{mxn(ov.onlineReservations.net)}</b>
                {ov.onlineReservations.refunds > 0 && (
                  <span className="ml-1 text-[11px] text-slate-400">(neto de {mxn(ov.onlineReservations.refunds)} reemb.)</span>
                )}
              </span>
            </div>
          )}

          {/* Top / Bottom performers */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card title="Más vendidos" icon={<TrendingUp className="h-4 w-4 text-emerald-600" />}
              action={
                <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-[11px]">
                  <button onClick={() => setTopMode('amount')} className={`rounded px-2 py-0.5 ${topMode === 'amount' ? 'bg-slate-900 text-white' : 'text-slate-500'}`}>Monto</button>
                  <button onClick={() => setTopMode('units')} className={`rounded px-2 py-0.5 ${topMode === 'units' ? 'bg-slate-900 text-white' : 'text-slate-500'}`}>Unidades</button>
                </div>
              }>
              <PerfTable rows={topItems ?? []} highlight="top" />
            </Card>
            <Card title="Menos vendidos" icon={<TrendingDown className="h-4 w-4 text-rose-500" />}>
              <p className="mb-2 -mt-2 text-[11px] text-slate-400">Candidatos a revisar/sacar del menú en el período.</p>
              <PerfTable rows={ov.bottom} highlight="bottom" />
            </Card>
          </div>

          {/* Mejores días + Hora pico */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card title="Mejores días" icon={<CalendarDays className="h-4 w-4 text-slate-500" />}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={ov.byWeekday} margin={{ top: 6, right: 8, left: -16, bottom: 0 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={48}
                    tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`)} />
                  <Tooltip formatter={(v: any) => mxn(v)} labelFormatter={(l) => `Día: ${l}`} />
                  <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                    {ov.byWeekday.map((d, i) => <Cell key={i} fill={bestDay && d.dow === bestDay.dow ? '#0f766e' : '#cbd5e1'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card title="Ventas por hora" icon={<Clock className="h-4 w-4 text-slate-500" />}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={businessHours} margin={{ top: 6, right: 8, left: -16, bottom: 0 }}>
                  <XAxis dataKey="hour" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} interval={1} tickFormatter={(h: number) => `${h}`} />
                  <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={48}
                    tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`)} />
                  <Tooltip formatter={(v: any) => mxn(v)} labelFormatter={(h) => `${h}:00 h`} />
                  <Bar dataKey="amount" radius={[3, 3, 0, 0]}>
                    {businessHours.map((h, i) => <Cell key={i} fill={peakHour && h.hour === peakHour.hour ? '#b45309' : '#cbd5e1'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* Franjas con top productos */}
          {ov.byFranja.length > 0 && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {ov.byFranja.map((f) => (
                <div key={f.franja} className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="font-medium text-slate-800">{f.franja}</p>
                    <span className="text-xs tabular-nums text-slate-500">{mxn(f.amount)}</span>
                  </div>
                  <ul className="space-y-0.5 text-[11px] text-slate-500">
                    {f.topItems.map((it, i) => (
                      <li key={i} className="flex justify-between"><span className="truncate">{i + 1}. {it.name}</span><span className="tabular-nums">{mxn(it.amount)}</span></li>
                    ))}
                    {f.topItems.length === 0 && <li className="text-slate-300">—</li>}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Registro de transacciones */}
      <Card title="Registro de transacciones" icon={<Receipt className="h-4 w-4 text-slate-500" />}>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <select className="input !w-auto !py-1.5 text-sm" value={weekday} onChange={(e) => setWeekday(e.target.value)}>
            {WEEKDAYS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
          </select>
          <span className="text-xs text-slate-400">Hora</span>
          <input className="input !w-16 !py-1.5 text-sm" type="number" min={0} max={23} placeholder="de" value={hourFrom} onChange={(e) => setHourFrom(e.target.value)} />
          <input className="input !w-16 !py-1.5 text-sm" type="number" min={0} max={23} placeholder="a" value={hourTo} onChange={(e) => setHourTo(e.target.value)} />
          {(weekday || hourFrom || hourTo) && (
            <button className="text-xs font-medium text-slate-500 hover:text-slate-900" onClick={() => { setWeekday(''); setHourFrom(''); setHourTo(''); }}>Limpiar</button>
          )}
          <span className="ml-auto text-xs text-slate-400">{txns.length} transacciones</span>
        </div>
        {txnLoading ? <p className="text-sm text-slate-500">Cargando…</p> : (
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2">Fecha y hora</th><th className="px-3 py-2">Mesa</th>
                  <th className="px-3 py-2 text-right">Items</th><th className="px-3 py-2 text-right">Subtotal</th>
                  <th className="px-3 py-2 text-right">IVA</th><th className="px-3 py-2 text-right">Propina</th>
                  <th className="px-3 py-2">Pago</th><th className="px-3 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {txns.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">Sin transacciones con estos filtros.</td></tr>}
                {txns.map((t) => (
                  <tr key={t.id}>
                    <td className="px-3 py-2 whitespace-nowrap">{new Date(t.closedAt).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}</td>
                    <td className="px-3 py-2 text-slate-600">{t.tableName ?? <span className="text-slate-400">Para llevar</span>}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{t.itemCount}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-500">{mxn(t.subtotal)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-500">{mxn(t.taxTotal)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-500">{mxn(t.tip)}</td>
                    <td className="px-3 py-2 text-slate-600">{METHOD_LABEL[t.method ?? ''] ?? t.method ?? '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium text-slate-800">{mxn(t.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function PerfTable({ rows, highlight }: { rows: ItemRow[]; highlight: 'top' | 'bottom' }) {
  if (rows.length === 0) return <p className="text-sm text-slate-400">Sin ventas en el período.</p>;
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-[11px] uppercase tracking-wider text-slate-400">
        <tr><th className="py-1">#</th><th className="py-1">Ítem</th><th className="py-1 text-right">Unidades</th><th className="py-1 text-right">Monto</th></tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((r, i) => (
          <tr key={r.name}>
            <td className="py-1.5 text-slate-400">{i + 1}</td>
            <td className="py-1.5 font-medium text-slate-800">{r.name}
              <span className={`ml-2 rounded px-1 py-0.5 text-[9px] uppercase ${r.kind === 'service' ? 'bg-indigo-50 text-indigo-600' : 'bg-emerald-50 text-emerald-700'}`}>{r.kind === 'service' ? 'serv' : 'prod'}</span>
            </td>
            <td className="py-1.5 text-right tabular-nums text-slate-600">{int(r.units)}</td>
            <td className={`py-1.5 text-right tabular-nums ${highlight === 'top' ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>{mxn(r.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
